import React from 'react';
import {
  getProviderStatusLabel,
  OrderStatusTypes,
  statusBadgeClassName,
} from '../../../types/OrderStatusTypes';
import { PaymentStatusTypes } from '../../../types/PaymentStatusTypes';
import { formatDate, formatDateTime } from './dateUtils';

function OrderStatusSpinner() {
  return (
    <svg
      className="order-detail-statusSpinner"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      aria-label="Загрузка статуса"
      role="status"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray="14.13 42.4"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 12 12"
          to="360 12 12"
          dur="0.85s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  );
}

const PAYMENT_LABELS = PaymentStatusTypes.labels;

function formatMoney(value) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function receiptBody(order) {
  const receipts = order?.receipts;
  if (!receipts || typeof receipts !== 'object') return null;
  return receipts.return || receipts.regular || null;
}

function receiptStatusLabel(regularReceipt, returnReceipt) {
  const parts = [];
  if (regularReceipt) parts.push('продажа ✅');
  if (returnReceipt) parts.push('возврат ✅');
  if (parts.length === 0) return '';
  return `${parts.join(', ')}`;
}

function receiptLink(body) {
  const link = body?.data?.link;
  const text = typeof link === 'string' ? link.trim() : '';
  if (!/^https?:\/\//i.test(text)) return '';
  return text;
}

function OrderInfoSection({
  order,
  isSystemOrder,
  onPaymentStatusOpen,
  onIssueReceipt,
  receiptSubmitting = false,
  receiptError = null,
  statusError = null,
  customStatuses = [],
}) {
  const customerName = order?.customerName ?? order?.customer_name ?? '—';
  const customerPhone = order?.customerPhone ?? order?.customer_phone ?? '—';
  const comments = order?.customerDescription ?? order?.customer_description ?? '';
  const servedOperator = order?.servedOperatorName ?? order?.served_operator_name ?? '—';
  const createdAt = order?.createdAt ?? order?.created_at;
  const updatedAt = order?.updatedAt ?? order?.updated_at;
  const statusId = order?.statusId ?? order?.status_id;
  const rawPaymentStatusId = order?.paymentStatusId ?? order?.payment_status_id;
  const paymentStatusId = Number(rawPaymentStatusId ?? PaymentStatusTypes.NOT_PAID);
  const showStatusSpinner = statusId == null || statusId === 0;
  const showPaymentSpinner = rawPaymentStatusId === null;
  const debtRaw = order?.debt;
  const debt = typeof debtRaw === 'number' ? debtRaw : parseFloat(debtRaw);
  const hasDebt = Number.isFinite(debt) && debt > 0;
  const isPaid = paymentStatusId === PaymentStatusTypes.PAID;
  const isCanceled = Number(statusId) === OrderStatusTypes.CANCELED;
  const paymentButtonLabel = isPaid ? 'Отменить оплату' : 'Оплатить';
  const [originFdInput, setOriginFdInput] = React.useState('');
  const [originHint, setOriginHint] = React.useState('');
  const returnReceipt = order?.receipts?.return || null;
  const regularReceipt = order?.receipts?.regular || null;
  const shownReceipt = receiptBody(order);
  const shownLink = receiptLink(shownReceipt);
  const receiptStatus = receiptStatusLabel(regularReceipt, returnReceipt);
  const needsOriginNumber = isCanceled && !isPaid && !regularReceipt && !returnReceipt;
  const canIssueReceipt = isCanceled
    ? Boolean(!returnReceipt && (regularReceipt || !isPaid))
    : isPaid && !shownReceipt;
  const showReceiptRow = Boolean(shownLink || shownReceipt || canIssueReceipt);
  const shownError = originHint || receiptError;

  const submitReceipt = () => {
    if (typeof onIssueReceipt !== 'function') return;
    if (!needsOriginNumber) {
      setOriginHint('');
      onIssueReceipt();
      return;
    }
    const number = Number(String(originFdInput).trim());
    if (!Number.isSafeInteger(number) || number <= 0) {
      setOriginHint('Укажите номер ФД');
      return;
    }
    setOriginHint('');
    onIssueReceipt(number);
  };

  return (
    <section className="order-detail-section">
      <dl className="order-detail-info">
        {!isSystemOrder ? (
          <>
            <div className="order-detail-row order-detail-row--withStatus">
              <div>
                <dt>Клиент</dt>
                <dd>{customerName}</dd>
              </div>
              <div className="order-detail-statusColumn">
                <span
                  className={
                    showStatusSpinner
                      ? "order-detail-status order-detail-status--pending"
                      : statusBadgeClassName('order-detail-status', statusId)
                  }
                  aria-busy={showStatusSpinner || undefined}
                >
                  {showStatusSpinner ? <OrderStatusSpinner /> : getProviderStatusLabel(statusId, customStatuses)}
                </span>
                {statusError ? (
                  <span className="order-detail-receiptError">{statusError}</span>
                ) : null}
              </div>
            </div>
            <div className="order-detail-row">
              <dt>Контакты</dt>
              <dd>{customerPhone}</dd>
            </div>
            {comments ? (
              <div className="order-detail-row">
                <dt>Комментарий</dt>
                <dd>{comments}</dd>
              </div>
            ) : null}
          </>
        ) : <>
          {comments && comments.length > 1 ? (
            <div className="order-detail-row">
              <dt>Комментарий</dt>
              <dd>{comments}</dd>
            </div>
          ) : null}
        </>}
        <div className="order-detail-row">
          <dt>Оператор</dt>
          <dd>{servedOperator}</dd>
        </div>
        <div className="order-detail-datesRow">
          <div className="order-detail-row">
            <dt>Создан</dt>
            <dd>{formatDate(createdAt)} {formatDateTime(createdAt)}</dd>
          </div>
          <div className="order-detail-row">
            <dt>Обновлён</dt>
            <dd>{formatDate(updatedAt)} {formatDateTime(updatedAt)}</dd>
          </div>
        </div>
        {!isSystemOrder ? (
          <div className="order-detail-row order-detail-row--payment">
            <div className="order-detail-row order-detail-row--withStatus">
              <div>
                <dt>Оплата</dt>
                <dd>
                  <span
                    className={
                      showPaymentSpinner
                        ? 'order-detail-status order-detail-status--pending'
                        : `order-detail-paymentStatus order-detail-paymentStatus--${paymentStatusId}`
                    }
                    aria-busy={showPaymentSpinner || undefined}
                  >
                    {showPaymentSpinner
                      ? <OrderStatusSpinner />
                      : (PAYMENT_LABELS[paymentStatusId] ?? `Статус ${paymentStatusId}`)}
                  </span>
                  {hasDebt && !isCanceled && !showPaymentSpinner ? (
                    <span className="order-detail-debt">Долг: {formatMoney(debt)} сом</span>
                  ) : null}
                </dd>
              </div>
              {typeof onPaymentStatusOpen === 'function' ? (
                <button
                  type="button"
                  className="order-detail-paymentBtn"
                  onClick={onPaymentStatusOpen}
                  disabled={showPaymentSpinner}
                >
                  {paymentButtonLabel}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
        {!isSystemOrder && showReceiptRow ? (
          <div className="order-detail-row order-detail-row--payment">
            <div className="order-detail-row order-detail-row--withStatus">
              <div>
                <dt>Чек</dt>
                <dd>
                  {receiptStatus ? (
                    <span className="order-detail-receiptStatus">{receiptStatus}</span>
                  ) : null}
                  {shownError ? (
                    <span className="order-detail-receiptError">{shownError}</span>
                  ) : null}
                </dd>
              </div>
              <div className="order-detail-receiptActions">
                {shownLink ? (
                  <button
                    type="button"
                    className="order-detail-paymentBtn"
                    onClick={() => window.open(shownLink, '_blank', 'noopener,noreferrer')}
                  >
                    Открыть чек
                  </button>
                ) : null}
                {canIssueReceipt && typeof onIssueReceipt === 'function' ? (
                  <>
                    {needsOriginNumber ? (
                      <input
                        className="order-detail-originFd"
                        inputMode="numeric"
                        placeholder="Номер ФД"
                        value={originFdInput}
                        onChange={(event) => setOriginFdInput(event.target.value)}
                        disabled={receiptSubmitting}
                      />
                    ) : null}
                    <button
                      type="button"
                      className="order-detail-paymentBtn"
                      onClick={submitReceipt}
                      disabled={receiptSubmitting}
                    >
                      {receiptSubmitting ? 'Отправка…' : 'Выбить чек'}
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </dl>
    </section>
  );
}

export default OrderInfoSection;
